import { useEffect, useRef, useState } from 'react';
import { subscribeToTotalUnreadCount } from '../services/messagingService';
import { subscribeToActiveQuestionCount } from '../services/firebase/questions';
import { getAccountsByTypeAndDateRange } from '../services/firestoreService';
import { isActiveClientAccount } from '../utils/accountLifecycle';

/** Window the CRM "new leads / new clients" sidebar badges count over. */
export const NEW_ACCOUNT_WINDOW_DAYS = 14;
// Refresh the new-account counts at most this often while navigating the CRM.
const NEW_ACCOUNT_REFRESH_MS = 5 * 60 * 1000;

/** Live unread message count across the user's conversations. */
export function useUnreadMessageCount(uid) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!uid) {
      setCount(0);
      return undefined;
    }
    return subscribeToTotalUnreadCount(uid, setCount);
  }, [uid]);
  return count;
}

/**
 * Live count of Open + In Progress questions. Pass the portal user's companyId
 * to scope to their company; pass `null` from the CRM to count all questions.
 * Nothing is subscribed while `enabled` is false.
 */
export function useActiveQuestionCount(accountId, enabled = true) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return undefined;
    }
    return subscribeToActiveQuestionCount(setCount, accountId || null);
  }, [accountId, enabled]);
  return count;
}

/**
 * Leads and clients created in the last NEW_ACCOUNT_WINDOW_DAYS — the same rules
 * the old CRM dashboard cards used (leads hidden from the kanban don't count;
 * clients must be active). Refetched on navigation, throttled.
 */
export function useNewAccountCounts({ enabled = true, includeLeads = true, refreshKey } = {}) {
  const [counts, setCounts] = useState({ leads: 0, clients: 0 });
  // Last successful fetch; the throttle only applies while includeLeads is unchanged.
  const lastFetchRef = useRef({ at: 0, includeLeads: null });

  useEffect(() => {
    if (!enabled) return undefined;
    const last = lastFetchRef.current;
    if (last.includeLeads === includeLeads && Date.now() - last.at < NEW_ACCOUNT_REFRESH_MS) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [leads, clients] = await Promise.all([
          includeLeads ? getAccountsByTypeAndDateRange('Leads', NEW_ACCOUNT_WINDOW_DAYS) : Promise.resolve([]),
          getAccountsByTypeAndDateRange('Clients', NEW_ACCOUNT_WINDOW_DAYS),
        ]);
        if (cancelled) return;
        setCounts({
          leads: leads.filter((lead) => lead.showOnKanban !== false).length,
          clients: clients.filter((client) => isActiveClientAccount(client)).length,
        });
        lastFetchRef.current = { at: Date.now(), includeLeads };
      } catch (error) {
        console.error('Error loading new lead/client counts:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, includeLeads, refreshKey]);

  return counts;
}
